import React, { useRef } from 'react';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import CTAButton from '@rsc/ui-components/tailwind/CTAButton';
import ProductCard from '@rsc/ui-components/tailwind/ProductCard';
import { generateSectionId } from '@rsc/ui-components/dept/utils/generateSectionId';
import type {
  SanityDeptCta,
  SanityGlobalProduct,
} from '@rsc/models/gatsby-graphql-types';
import { Button } from '@rsc/ui-components/shadcn/Button';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';

interface Props {
  block: {
    headline: string;
    copy: string;
    products: SanityGlobalProduct[];
    cta: SanityDeptCta;
  };
}

export default function CarouselRowProductsFull({ block }: Readonly<Props>) {
  const { headline, copy, cta, products } = block;
  const { tt } = useUiComponentsInternationalContext();
  const scrollSnapRef = useRef<HTMLDivElement>(null);

  const scrollToSlide = (direction: 'next' | 'prev') => {
    if (scrollSnapRef.current) {
      const container = scrollSnapRef.current;
      const slideWidth = container.offsetWidth;

      container.scrollBy({
        left: direction === 'next' ? slideWidth : -slideWidth,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div {...generateSectionId(block)} className="px-6 md:p-20 py-20">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4 md:gap-10 mb-10">
        <div className="flex flex-col">
          <h2 className="text-4xl font-black">{headline}</h2>
          {copy && <p className="w-full lg:w-[32.5rem]">{copy}</p>}
        </div>
        <CTAButton className="hidden lg:flex" cta={cta} variant="primary" />
      </div>
      <div
        className="overflow-x-auto snap-x snap-mandatory scroll-smooth flex gap-4"
        ref={scrollSnapRef}
      >
        {products?.map((product, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`${product.id}-${i}`}
            className="snap-center flex-shrink-0 max-w-[242px] xl:max-w-[269.111px]"
          >
            <ProductCard product={product} />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 pl-1 mt-3">
        <Button
          className="w-8 h-8 rounded-full"
          onClick={() => {
            scrollToSlide('prev');
          }}
          aria-label={tt(s => s.blocksDeptComponents.previousButtonAriaLabel)}
        >
          <MdArrowBack size="24px" className="shrink-0" />
        </Button>
        <Button
          className="w-8 h-8 rounded-full"
          onClick={() => {
            scrollToSlide('next');
          }}
          aria-label={tt(s => s.blocksDeptComponents.nextButtonAriaLabel)}
        >
          <MdArrowForward size="24px" className="shrink-0" />
        </Button>
      </div>
      <CTAButton
        className="flex w-full lg:hidden mt-3"
        cta={cta}
        variant="primary"
      />
    </div>
  );
}
